import { cn } from 'lib/utils'
import { Pencil } from 'lucide-react'
import * as React from 'react'

/**
 * An always-live editable value: text that reads as plain copy at rest and is
 * a real form field at every moment, with no read/edit mode swap.
 *
 * Spec: playbook/design-system/components.md ("Editable card row").
 *
 * Why there is no display mode. The pattern this replaces rendered a
 * `<button>` at rest and mounted an `<input>` on click. That button sat in the
 * tab order and painted a focus ring, but a button swallows character keys —
 * so tabbing to a field and typing did nothing at all, with no hint that
 * Enter was the way in. The swap also reflowed the page: a value that wrapped
 * to three lines as text collapsed to one line as an `<input>`, so every row
 * below it jumped on focus and jumped back on blur.
 *
 * Why a `<textarea>` even for single-line values. An `<input>` clips a long
 * value horizontally, so at rest you cannot read your own business name; and a
 * name long enough to wrap renders on two lines as text but one line in an
 * input, which reintroduces the reflow this component exists to remove. A
 * wrapping textarea makes the resting box and the focused box identical at
 * every value length. `singleLine` controls Enter and paste semantics, not
 * wrapping.
 *
 * The wrapper owns the underline and the typography; the textarea carries no
 * chrome of its own (`border-0 p-0 m-0`, font inherited). That keeps the
 * field's text metrics identical to a plain text node, which is what lets
 * `overlay` sit exactly on top of it.
 */
export interface EditableValueProps {
  value: string
  /**
   * Fires on every keystroke with the raw, untrimmed value. Pass this when
   * validation has to track the field as the user types (e.g. a length gate
   * on a submit button); omit it for save-on-blur-only fields.
   */
  onChange?: (next: string) => void
  /**
   * Fires with the trimmed value on blur, and on Enter, but only when it
   * differs from the value at the moment the field took focus.
   */
  onSave?: (next: string) => void
  placeholder: string
  /** Accessible name. Supply this or `labelledBy` — never neither. */
  label?: string
  /** id of an existing visible label, e.g. an `.editable-row > .row-label`. */
  labelledBy?: string
  /**
   * Enter commits and blurs rather than inserting a newline, and newlines in
   * pasted text collapse to spaces. Also turns off autocapitalise and
   * autocorrect, which are wrong on an identifier, and makes keyboard focus
   * select the value the way a native `<input>` does.
   */
  singleLine?: boolean
  /** Typography and text colour. Applied to the wrapper; the field inherits. */
  className?: string
  /** Paints the T3 error border and sets `aria-invalid`. */
  invalid?: boolean
  /** Dashed editable underline. Off for bare identity-card text. */
  underline?: boolean
  /**
   * Cap the field at this many lines; longer content scrolls inside it.
   *
   * In lines rather than pixels on purpose. A px cap is only ever a whole
   * number of lines at one font size, so the same value that looked right on
   * desktop sliced a row of text in half horizontally at mobile type sizes.
   * Resolved against the field's own computed line-height, so it stays a clean
   * multiple at every breakpoint.
   */
  maxLines?: number
  /**
   * This value cannot be empty: clearing it and leaving snaps the field back
   * to what it held on focus rather than committing the blank.
   *
   * Needed because there is no display mode to fall back to. Under the old
   * click-to-edit pattern a caller could just drop an empty save and the
   * static text would reappear; an always-live field would sit there showing
   * the user their name is gone while the store still has it.
   *
   * Leave it off where empty is a state the form itself reports on — the
   * onboarding name field wants the blank to stick so its "must be at least 2
   * characters" message can show and gate the CTA.
   */
  revertWhenEmpty?: boolean
  /** Hide the pencil affordance. */
  hidePencil?: boolean
  /** Pencil size in px. */
  pencilSize?: number
  disabled?: boolean
  /**
   * Stands in for the field's own text: rendered on top of the field while
   * the field's text goes transparent. Exists for the onboarding
   * Scanning→BrandCard morph, which needs a framer `layoutId` on the value.
   * The box keeps its real height either way.
   */
  overlay?: React.ReactNode
}

export const EditableValue: React.FC<EditableValueProps> = ({
  value,
  onChange,
  onSave,
  placeholder,
  label,
  labelledBy,
  singleLine = false,
  className,
  invalid,
  underline = true,
  maxLines,
  revertWhenEmpty = false,
  hidePencil = false,
  pencilSize = 12,
  disabled = false,
  overlay,
}) => {
  const ref = React.useRef<HTMLTextAreaElement>(null)
  const [draft, setDraft] = React.useState(value)

  /**
   * The value as of the moment the field took focus — the target for Escape,
   * and the baseline for "did this actually change?" on commit. It cannot be
   * derived from `value`: callers passing `onChange` get `value` rewritten on
   * every keystroke, so by blur time `value` is the edited text.
   */
  const baselineRef = React.useRef(value)

  /**
   * Set while Escape unwinds an edit. Escape blurs the field to leave it, and
   * that blur would otherwise run `commit()` and save the draft Escape just
   * discarded.
   */
  const revertingRef = React.useRef(false)

  /**
   * Set between pointer-down and the focus it causes, to tell a click apart
   * from a Tab. See the `select()` call in `onFocus`.
   */
  const pointerFocusRef = React.useRef(false)

  // Pick up committed values that change from outside (a server refresh, or a
  // parent normalising what we just saved). A no-op for callers passing
  // `onChange`, where `value` already mirrors `draft`.
  React.useEffect(() => setDraft(value), [value])

  /**
   * Height tracks content, so the field occupies the same box focused or not.
   * `height: auto` first, otherwise `scrollHeight` can only ever grow.
   */
  React.useLayoutEffect(() => {
    const el = ref.current
    if (!el) return
    const grow = () => {
      el.style.height = 'auto'
      const full = el.scrollHeight
      if (!maxLines) {
        el.style.height = `${full}px`
        el.style.overflowY = 'hidden'
        return
      }
      // Snap the cap to whole lines, so a capped field never clips a row of
      // text through the middle of the glyphs.
      const cs = getComputedStyle(el)
      const parsed = parseFloat(cs.lineHeight)
      // `line-height: normal` doesn't parse; approximate it the way browsers do.
      const line = Number.isFinite(parsed) ? parsed : parseFloat(cs.fontSize) * 1.2
      const cap = Math.round(line * maxLines)
      el.style.height = `${Math.min(full, cap)}px`
      el.style.overflowY = full > cap ? 'auto' : 'hidden'
    }
    grow()
    // A width change re-wraps the text with no keystroke to trigger it, so
    // watch the element. Width-only: `grow` writes height, and reacting to
    // our own write would loop.
    let lastWidth = el.clientWidth
    const ro = new ResizeObserver(() => {
      if (el.clientWidth === lastWidth) return
      lastWidth = el.clientWidth
      grow()
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [draft, maxLines])

  const commit = () => {
    pointerFocusRef.current = false
    if (revertingRef.current) {
      revertingRef.current = false
      return
    }
    const next = draft.trim()
    if (!next && revertWhenEmpty) {
      setDraft(baselineRef.current)
      onChange?.(baselineRef.current)
      return
    }
    // Normalise away stray edge whitespace even when nothing is saved, so the
    // field doesn't sit there looking indented.
    if (next !== draft) {
      setDraft(next)
      onChange?.(next)
    }
    if (next !== baselineRef.current) {
      baselineRef.current = next
      onSave?.(next)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Shift+Enter is the newline escape hatch on prose fields. On single-line
    // fields no chord inserts a newline.
    if (e.key === 'Enter' && (singleLine || !e.shiftKey)) {
      e.preventDefault()
      ref.current?.blur()
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      revertingRef.current = true
      setDraft(baselineRef.current)
      onChange?.(baselineRef.current)
      ref.current?.blur()
    }
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
    if (!singleLine) return
    const pasted = e.clipboardData.getData('text')
    if (!/[\r\n]/.test(pasted)) return
    // A pasted multi-line blob would otherwise silently grow a single-line
    // field into a block. Flatten runs of newlines to one space.
    e.preventDefault()
    const el = e.currentTarget
    const flat = pasted.replace(/\s*[\r\n]+\s*/g, ' ')
    const start = el.selectionStart ?? draft.length
    const end = el.selectionEnd ?? draft.length
    const next = draft.slice(0, start) + flat + draft.slice(end)
    setDraft(next)
    onChange?.(next)
    const caret = start + flat.length
    requestAnimationFrame(() => el.setSelectionRange(caret, caret))
  }

  // Autocapitalise and autocorrect are wrong on an identifier — a "corrected"
  // business name is a bug, not a fix — and harmless on prose.
  //
  // Spellcheck is off on every field, prose included. These fields arrive
  // pre-filled with brand copy, which is mostly proper nouns, so the default
  // state every user first sees would be their own brand name under a red
  // squiggle. That reads as "you got something wrong" on a screen whose whole
  // job is "here's what we found, looks right?".
  const proseAttrs = singleLine
    ? { autoComplete: 'off', autoCorrect: 'off', autoCapitalize: 'off' }
    : {}

  return (
    <div
      className={cn(
        'group/edit relative flex w-full items-start gap-1.5',
        underline && [
          'border-b border-dashed border-line pb-0.5 transition-colors',
          // Suppressed while invalid: `focus-within:border-acid` is a later
          // variant than the plain error border below, so it would win the
          // cascade and paint an invalid field acid the moment you focused it
          // to fix it. An invalid field stays red until it is valid.
          !disabled &&
            !invalid &&
            'focus-within:border-solid focus-within:border-acid hover:border-paper-dim',
        ],
        className,
        // After `className` so the error border wins the twMerge conflict
        // while the caller's font size and weight survive. Border only —
        // matches how <Input variant="dark"> renders aria-invalid.
        underline && invalid && 'border-solid border-[color:var(--alert-error-border)]'
      )}
    >
      <textarea
        ref={ref}
        rows={1}
        value={draft}
        disabled={disabled}
        onChange={e => {
          setDraft(e.target.value)
          onChange?.(e.target.value)
        }}
        onMouseDown={() => {
          pointerFocusRef.current = true
        }}
        onTouchStart={() => {
          pointerFocusRef.current = true
        }}
        onFocus={e => {
          baselineRef.current = draft
          // Arriving by keyboard, each field should behave like the native
          // element it stands in for. A single-line identifier mimics
          // <input>: the value comes in selected, so typing replaces it
          // (a bare textarea would instead drop the caret at index 0 and
          // prepend, which nobody expects of a name field). Prose keeps the
          // native textarea caret — select-all there would mean one keystroke
          // wipes a paragraph the user meant to edit.
          //
          // Pointer focus always keeps the caret where the user aimed it.
          if (singleLine && !pointerFocusRef.current) e.currentTarget.select()
          pointerFocusRef.current = false
        }}
        onBlur={commit}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        placeholder={placeholder}
        spellCheck={false}
        aria-label={labelledBy ? undefined : label}
        aria-labelledby={labelledBy}
        aria-invalid={invalid || undefined}
        {...proseAttrs}
        className={cn(
          'm-0 min-w-0 flex-1 resize-none border-0 bg-transparent p-0 outline-none',
          'text-[color:inherit] caret-[color:var(--acid)]',
          'placeholder:text-paper-faint placeholder:italic',
          'disabled:cursor-not-allowed disabled:opacity-50',
          // The overlay is standing in for this text; keep the box, drop the
          // glyphs. Not `invisible`, which would also kill the caret.
          overlay && 'text-transparent'
        )}
      />
      {overlay && <div className="pointer-events-none absolute inset-0">{overlay}</div>}
      {!hidePencil && !disabled && (
        <Pencil
          size={pencilSize}
          aria-hidden
          // Always in the layout, only opacity animates — revealing it on
          // hover must not re-wrap the text beside it.
          className="mt-[0.3em] shrink-0 text-acid opacity-0 transition-opacity group-focus-within/edit:opacity-100 group-hover/edit:opacity-50"
        />
      )}
    </div>
  )
}
